import invariant from 'invariant';
import React, { FC, useCallback, useRef } from 'react';
import { ContextMenuTrigger } from 'react-contextmenu';
import { FormattedMessage } from 'react-intl';
import { useTrackActions } from '../../../../../utils/hooks/bi/useTrackActions';
import { ActionClickOrigin } from '../../../../../utils/hooks/bi/useTrackActions/useTrackActions.types';
import { TrackBoardClickTarget, useTrackBoardAnalytics } from '../../../../../utils/hooks/bi/useTrackBoardAnalytics';
import { useOpenContextMenu } from '../../../../../utils/hooks/useOpenContextMenu';
import { useSources } from '../../../../../utils/hooks/useSources';
import { isBoard } from '../../../../../utils/media-manager-sdk/boards';
import { isSystemFolder } from '../../../../../utils/media-manager-sdk/items';
import { IconButton, Text, Tooltip, WixIcons } from '../../../../../components/common';
import { BoardsContextMenuTrigger } from '../../../../../components/common/BoardsContextMenu';
import { Separator } from '../Separator';
import styles from './Actions.scss';
import { IOuter } from './Actions.types';

export const Actions: FC<IOuter> = ({
  selectedItems,
  currentFolder,
  className,
  dataHook,
  children,
  isMoreActionsVisible,
  source,
  isMultipleBoardsSelected,
  contextMenuId,
  onRemoveItemsFromBoard,
}) => {
  invariant(
    (!currentFolder && selectedItems) || (currentFolder && !selectedItems),
    'provide one of: selectedItems or currentFolder',
  );

  const { isBoardRoot, isBoardsRoot, isTrash } = useSources({ source });

  const contextMenuTriggerRef = useRef();
  const openContextMenu = useOpenContextMenu(contextMenuTriggerRef);

  const boardsContextMenuTriggerRef = useRef();
  const openBoardsContextMenu = useOpenContextMenu(boardsContextMenuTriggerRef);

  const { trackBoardClick } = useTrackBoardAnalytics();

  const onAddToBoardClick = useCallback(
    (event) => {
      openBoardsContextMenu(event);

      trackBoardClick(selectedItems && selectedItems.length ? selectedItems : currentFolder, {
        target: TrackBoardClickTarget.Add,
        clickOrigin: ActionClickOrigin.InfoPanel,
      });
    },
    [openBoardsContextMenu, trackBoardClick, selectedItems, currentFolder],
  );

  const { trackActionsOpenClick } = useTrackActions();
  const getParamsForMoreActionContextMenu = useCallback(() => {
    const clickOrigin = ActionClickOrigin.InfoPanelActionDots;
    const itemsToTrack = currentFolder ? [currentFolder] : selectedItems;

    trackActionsOpenClick(itemsToTrack, clickOrigin);

    return {
      contextMenuOrigin: clickOrigin,
    };
  }, [selectedItems, currentFolder, trackActionsOpenClick]);

  const handleRemoveSelectedItemsFromBoard = useCallback(() => {
    const activeItems = selectedItems && selectedItems.length ? selectedItems : [currentFolder];
    const clickOrigin = ActionClickOrigin.InfoPanel;

    onRemoveItemsFromBoard(activeItems, { clickOrigin });

    trackBoardClick(activeItems, {
      target: TrackBoardClickTarget.Remove,
      clickOrigin,
    });
  }, [selectedItems, currentFolder, trackBoardClick, onRemoveItemsFromBoard]);

  const shouldDisplayBoardsActions =
    !isBoardsRoot &&
    (Boolean(selectedItems && selectedItems.length) || (!isBoard(currentFolder) && !isSystemFolder(currentFolder)));

  return (
    <div className={className} data-hook={dataHook}>
      <div className={styles.header}>
        <Text weight="bold" size="medium" dataHook="header">
          <FormattedMessage id="actions.actions" />
        </Text>

        <div className={styles.actionButtons}>
          {shouldDisplayBoardsActions &&
            !isTrash &&
            (isBoardRoot ? (
              <Tooltip
                content={<FormattedMessage id="actions.remove-from-board" />}
                placement="left"
                appendTo="viewport"
              >
                <IconButton
                  skin="standard"
                  priority="secondary"
                  size="tiny"
                  onClick={handleRemoveSelectedItemsFromBoard}
                  dataHook="remove-from-board-action"
                >
                  <WixIcons.BookmarkFilled />
                </IconButton>
              </Tooltip>
            ) : (
              <Tooltip content={<FormattedMessage id="actions.add-to-board" />} placement="left" appendTo="viewport">
                <BoardsContextMenuTrigger
                  contextMenuRef={boardsContextMenuTriggerRef}
                  clickOrigin={ActionClickOrigin.InfoPanel}
                >
                  <IconButton
                    skin="standard"
                    priority="secondary"
                    size="tiny"
                    onClick={onAddToBoardClick}
                    dataHook="add-to-board-action"
                  >
                    <WixIcons.Bookmark />
                  </IconButton>
                </BoardsContextMenuTrigger>
              </Tooltip>
            ))}

          {isMoreActionsVisible && (
            <IconButton
              size="tiny"
              dataHook="actions.more-actions-button"
              className={styles.action}
              onClick={openContextMenu}
            >
              <ContextMenuTrigger
                renderTag="span"
                id={contextMenuId}
                holdToDisplay={-1}
                ref={contextMenuTriggerRef}
                collect={getParamsForMoreActionContextMenu}
              >
                <WixIcons.MoreSmall />
              </ContextMenuTrigger>
            </IconButton>
          )}
        </div>
      </div>

      {children}

      {!isMultipleBoardsSelected && <Separator dataHook="separator" />}
    </div>
  );
};
